/**
 * Единая модель уведомлений (Header Notification Center).
 * Источники событий и человекочитаемые подписи для UI.
 */

import i18n from '../../i18n/config';

import type { NotificationSource, NotificationSeverity } from '../store/notificationStore';

interface NotificationText {
  severity: NotificationSeverity;
  title: string;
  message: string;
}

type BuildNotificationKind = 'strategy' | 'indicator';

function getCommonAlertTitles(): Array<[RegExp, string]> {
  return [
    [/instance.?down|service.?down|target.?down/i, i18n.t('notifications:alerts.titles.serviceDown')],
    [/latency|slow|timeout/i, i18n.t('notifications:alerts.titles.slow')],
    [/error.?rate|errors?/i, i18n.t('notifications:alerts.titles.errorRate')],
    [/memory|oom/i, i18n.t('notifications:alerts.titles.memory')],
    [/cpu/i, i18n.t('notifications:alerts.titles.cpu')],
    [/disk|storage|filesystem/i, i18n.t('notifications:alerts.titles.disk')],
    [/redis|cache/i, i18n.t('notifications:alerts.titles.redis')],
    [/postgres|database|db/i, i18n.t('notifications:alerts.titles.postgres')],
    [/kafka|queue/i, i18n.t('notifications:alerts.titles.kafka')],
    [/websocket|ws/i, i18n.t('notifications:alerts.titles.websocket')],
    [/binance|exchange/i, i18n.t('notifications:alerts.titles.binance')],
  ];
}

function getCommonAlertSources(): Array<[RegExp, string]> {
  return [
    [/alertmanager|prometheus|monitor/i, i18n.t('notifications:alerts.sources.monitoring')],
    [/postgres|database|db/i, i18n.t('notifications:alerts.sources.postgres')],
    [/redis|cache/i, i18n.t('notifications:alerts.sources.redis')],
    [/kafka|queue/i, i18n.t('notifications:alerts.sources.kafka')],
    [/websocket|ws/i, i18n.t('notifications:alerts.sources.websocket')],
    [/binance|exchange/i, i18n.t('notifications:alerts.sources.binance')],
    [/api|backend/i, i18n.t('notifications:alerts.sources.api')],
    [/ui|frontend/i, i18n.t('notifications:alerts.sources.ui')],
  ];
}

function cleanupText(value: string): string {
  return value.replace(/\s+/g, ' ').replace(/\s+([,.;:!?])/g, '$1').trim();
}

function uppercaseFirst(value: string): string {
  return value ? `${value.charAt(0).toUpperCase()}${value.slice(1)}` : value;
}

function splitTechnicalWords(value: string): string {
  return cleanupText(
    value
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\b([A-Z]{2,})([A-Z][a-z])/g, '$1 $2'),
  );
}

export function getNotificationSourceLabel(source: NotificationSource): string {
  return i18n.t(`notifications:source.${source}`);
}

export function getNotificationSeverityLabel(severity: NotificationSeverity): string {
  return i18n.t(`notifications:severity.${severity}`);
}

export function formatApiDetail(value: unknown): string {
  if (typeof value === 'string') return value.trim();
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.map((item) => formatApiDetail(item)).filter(Boolean).join('; ');
  }
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const msg = record.msg ?? record.message;
    if (typeof msg === 'string' && msg.trim()) {
      const loc = record.loc;
      if (Array.isArray(loc) && loc.length) {
        return `${loc.map(String).join('.')}: ${msg.trim()}`;
      }
      return msg.trim();
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value).trim();
}

export function humanizeDetailText(
  value: unknown,
  fallback = i18n.t('notifications:detail.fallback'),
): string {
  let text = typeof value === 'string' ? value.trim() : formatApiDetail(value);
  if (!text) return fallback;

  const replacements: Array<[RegExp, string | ((substring: string, ...args: string[]) => string)]> = [
    [/\bSession START\b/gi, i18n.t('notifications:detail.replacements.sessionStart')],
    [/\bbackfill\b/gi, i18n.t('notifications:detail.replacements.backfill')],
    [/\bbacktest\b/gi, i18n.t('notifications:detail.replacements.backtest')],
    [/\bruntime\b/gi, i18n.t('notifications:detail.replacements.runtime')],
    [/\blifecycle\b/gi, i18n.t('notifications:detail.replacements.lifecycle')],
    [/\bpersistence\b/gi, i18n.t('notifications:detail.replacements.persistence')],
    [/\bfill persisted\b/gi, i18n.t('notifications:detail.replacements.fillPersisted')],
    [/\bfill\b/gi, i18n.t('notifications:detail.replacements.fill')],
    [/\bexecution\b/gi, i18n.t('notifications:detail.replacements.execution')],
    [/\bexec requested\b/gi, i18n.t('notifications:detail.replacements.execRequested')],
    [/\border\b/gi, i18n.t('notifications:detail.replacements.order')],
    [/\borders\b/gi, i18n.t('notifications:detail.replacements.orders')],
    [/\bsignal\b/gi, i18n.t('notifications:detail.replacements.signal')],
    [/\bsignals\b/gi, i18n.t('notifications:detail.replacements.signals')],
    [/\bposition\b/gi, i18n.t('notifications:detail.replacements.position')],
    [/\bpositions\b/gi, i18n.t('notifications:detail.replacements.positions')],
    [/\bstrategy specs?\b/gi, i18n.t('notifications:detail.replacements.strategySpecs')],
    [/\bindicator specs?\b/gi, i18n.t('notifications:detail.replacements.indicatorSpecs')],
    [/\bstrategy spec\b/gi, i18n.t('notifications:detail.replacements.strategySpec')],
    [/\bindicator spec\b/gi, i18n.t('notifications:detail.replacements.indicatorSpec')],
    [/\boverrides\b/gi, i18n.t('notifications:detail.replacements.overrides')],
    [/\blegacy\b/gi, i18n.t('notifications:detail.replacements.legacy')],
    [/\bparams\b/gi, i18n.t('notifications:detail.replacements.params')],
    [/\bnot_ready\b/gi, i18n.t('notifications:detail.replacements.notReady')],
    [/\bdegraded\b/gi, i18n.t('notifications:detail.replacements.degraded')],
    [/\bREJECTED\b/g, i18n.t('notifications:detail.replacements.rejected')],
    [/\bFAILED\b/g, i18n.t('notifications:detail.replacements.failed')],
    [
      /Request failed with status code (\d+)/gi,
      (_, code: string) => i18n.t('notifications:detail.replacements.requestFailedWithCode', { code }),
    ],
    ...( /\bHTTP \d+\b/.test(text)
      ? []
      : [[/\bRequest failed\b/gi, i18n.t('notifications:detail.replacements.requestFailed')] as [RegExp, string]]),
  ];

  for (const [pattern, replacement] of replacements) {
    text =
      typeof replacement === 'function'
        ? text.replace(pattern, replacement)
        : text.replace(pattern, replacement);
  }

  return uppercaseFirst(cleanupText(text));
}

export function humanizeSignalNotification(signalTypeRaw: string, symbolRaw?: string): NotificationText {
  const signalType = String(signalTypeRaw || '').toUpperCase();
  const symbol = String(symbolRaw || '').trim().toUpperCase();
  const market = symbol
    ? i18n.t('notifications:signal.marketBySymbol', { symbol })
    : i18n.t('notifications:signal.marketFallback');

  switch (signalType) {
    case 'BUY':
      return {
        severity: 'info',
        title: i18n.t('notifications:signal.buy.title'),
        message: i18n.t('notifications:signal.buy.message', { market }),
      };
    case 'SELL':
      return {
        severity: 'info',
        title: i18n.t('notifications:signal.sell.title'),
        message: i18n.t('notifications:signal.sell.message', { market }),
      };
    case 'EXIT':
      return {
        severity: 'info',
        title: i18n.t('notifications:signal.exit.title'),
        message: i18n.t('notifications:signal.exit.message', { market }),
      };
    case 'HOLD':
      return {
        severity: 'info',
        title: i18n.t('notifications:signal.hold.title'),
        message: i18n.t('notifications:signal.hold.message', { market }),
      };
    case 'ALERT':
      return {
        severity: 'warning',
        title: i18n.t('notifications:signal.alert.title'),
        message: i18n.t('notifications:signal.alert.message', { market }),
      };
    default:
      return {
        severity: 'info',
        title: i18n.t('notifications:signal.default.title'),
        message: symbol
          ? i18n.t('notifications:signal.default.messageWithSymbol', { symbol })
          : i18n.t('notifications:signal.default.messageNoSymbol'),
      };
  }
}

export function humanizeExecutionNotification(statusRaw: string, symbolRaw?: string): NotificationText {
  const status = String(statusRaw || '').toUpperCase();
  const symbol = String(symbolRaw || '').trim().toUpperCase();
  const market = symbol
    ? i18n.t('notifications:execution.marketBySymbol', { symbol })
    : i18n.t('notifications:execution.marketFallback');

  switch (status) {
    case 'FILLED':
      return {
        severity: 'info',
        title: i18n.t('notifications:execution.filled.title'),
        message: i18n.t('notifications:execution.filled.message', { market }),
      };
    case 'PARTIALLY_FILLED':
      return {
        severity: 'info',
        title: i18n.t('notifications:execution.partial.title'),
        message: i18n.t('notifications:execution.partial.message', { market }),
      };
    case 'REJECTED':
      return {
        severity: 'critical',
        title: i18n.t('notifications:execution.rejected.title'),
        message: i18n.t('notifications:execution.rejected.message', { market }),
      };
    case 'CANCELLED':
    case 'CANCELED':
      return {
        severity: 'warning',
        title: i18n.t('notifications:execution.cancelled.title'),
        message: i18n.t('notifications:execution.cancelled.message', { market }),
      };
    case 'EXEC_REQUESTED':
      return {
        severity: 'info',
        title: i18n.t('notifications:execution.requested.title'),
        message: i18n.t('notifications:execution.requested.message', { market }),
      };
    case 'NEW':
    case 'PENDING':
      return {
        severity: 'info',
        title: i18n.t('notifications:execution.accepted.title'),
        message: i18n.t('notifications:execution.accepted.message', { market }),
      };
    case 'ERROR':
    case 'FAILED':
    case 'EXPIRED':
      return {
        severity: 'critical',
        title: i18n.t('notifications:execution.failed.title'),
        message: i18n.t('notifications:execution.failed.message', { market }),
      };
    default:
      return {
        severity: status.includes('REJECT') || status.includes('ERROR') ? 'critical' : 'info',
        title: i18n.t('notifications:execution.default.title'),
        message: symbol
          ? i18n.t('notifications:execution.default.messageWithSymbol', { symbol })
          : i18n.t('notifications:execution.default.messageNoSymbol'),
      };
  }
}

export function humanizeHealthNotification(statusRaw: string): NotificationText {
  const status = String(statusRaw || '').toLowerCase();

  if (status === 'not_ready') {
    return {
      severity: 'warning',
      title: i18n.t('notifications:health.notReady.title'),
      message: i18n.t('notifications:health.notReady.message'),
    };
  }

  if (status === 'degraded') {
    return {
      severity: 'warning',
      title: i18n.t('notifications:health.degraded.title'),
      message: i18n.t('notifications:health.degraded.message'),
    };
  }

  return {
    severity: 'info',
    title: i18n.t('notifications:health.default.title'),
    message: i18n.t('notifications:health.default.message'),
  };
}

/**
 * Потеря и восстановление связи с API. Колокольчик — единственный канал уведомлений,
 * поэтому «сервер молчит» должно быть видно здесь, а не только в панели здоровья
 * (2026-09-10 API простоял почти два часа, и интерфейс об этом не сообщил).
 */
export function humanizeConnectivityNotification(state: 'lost' | 'restored'): NotificationText {
  if (state === 'lost') {
    return {
      severity: 'critical',
      title: i18n.t('notifications:health.apiUnavailable.title'),
      message: i18n.t('notifications:health.apiUnavailable.message'),
    };
  }
  return {
    severity: 'info',
    title: i18n.t('notifications:health.apiRestored.title'),
    message: i18n.t('notifications:health.apiRestored.message'),
  };
}

/** Проверки readiness, которые явно провалены (false); null/отсутствие — «не применимо». */
export function failedReadinessChecks(checks: unknown): string[] {
  if (!checks || typeof checks !== 'object' || Array.isArray(checks)) return [];
  return Object.entries(checks as Record<string, unknown>)
    .filter(([, value]) => value === false)
    .map(([name]) => name)
    .sort();
}

export function humanizeReadinessNotification(failedChecks: string[]): NotificationText {
  const labels = failedChecks.map((check) =>
    i18n.t(`notifications:health.checks.${check}`, { defaultValue: splitTechnicalWords(check) }),
  );
  return {
    severity: 'warning',
    title: i18n.t('notifications:health.notReadyChecks.title'),
    message: labels.length
      ? i18n.t('notifications:health.notReadyChecks.message', { checks: labels.join(', ') })
      : i18n.t('notifications:health.notReady.message'),
  };
}

export function humanizeBuildFailureNotification(
  kind: BuildNotificationKind,
  count: number,
): NotificationText {
  return {
    severity: 'warning',
    title: i18n.t(`notifications:build.${kind}.title`),
    message: i18n.t(`notifications:build.${kind}.message`, { count }),
  };
}

export function humanizeRuntimeNotification(
  agentIdRaw: string,
  lifecycleStatusRaw: string,
  lifecycleReasonRaw?: string | null,
): NotificationText {
  const agentId = String(agentIdRaw || '').trim();
  const lifecycleStatus = String(lifecycleStatusRaw || '').trim().toLowerCase();
  const lifecycleReason = humanizeDetailText(lifecycleReasonRaw, '');
  const agentLabel = agentId
    ? i18n.t('notifications:runtime.agentWithId', { agentId })
    : i18n.t('notifications:runtime.agentGeneric');

  if (lifecycleReason) {
    switch (lifecycleStatus) {
      case 'rejected':
        return { severity: 'warning', title: i18n.t('notifications:runtime.titles.rejected'), message: lifecycleReason };
      case 'exec_requested':
        return { severity: 'info', title: i18n.t('notifications:runtime.titles.exec_requested'), message: lifecycleReason };
      case 'signal_only':
        return { severity: 'info', title: i18n.t('notifications:runtime.titles.signal_only'), message: lifecycleReason };
      case 'fill_persisted':
        return { severity: 'info', title: i18n.t('notifications:runtime.titles.fill_persisted'), message: lifecycleReason };
      case 'filled_open_position':
        return { severity: 'info', title: i18n.t('notifications:runtime.titles.filled_open_position'), message: lifecycleReason };
      default:
        return { severity: 'info', title: i18n.t('notifications:runtime.titles.default'), message: lifecycleReason };
    }
  }

  switch (lifecycleStatus) {
    case 'rejected':
      return {
        severity: 'warning',
        title: i18n.t('notifications:runtime.titles.rejected'),
        message: i18n.t('notifications:runtime.messages.rejected', { agentLabel }),
      };
    case 'exec_requested':
      return {
        severity: 'info',
        title: i18n.t('notifications:runtime.titles.exec_requested'),
        message: i18n.t('notifications:runtime.messages.exec_requested', { agentLabel }),
      };
    case 'signal_only':
      return {
        severity: 'info',
        title: i18n.t('notifications:runtime.titles.signal_only'),
        message: i18n.t('notifications:runtime.messages.signal_only', { agentLabel }),
      };
    case 'fill_persisted':
      return {
        severity: 'info',
        title: i18n.t('notifications:runtime.titles.fill_persisted'),
        message: i18n.t('notifications:runtime.messages.fill_persisted', { agentLabel }),
      };
    case 'filled_open_position':
      return {
        severity: 'info',
        title: i18n.t('notifications:runtime.titles.filled_open_position'),
        message: i18n.t('notifications:runtime.messages.filled_open_position', { agentLabel }),
      };
    default:
      return {
        severity: 'info',
        title: i18n.t('notifications:runtime.titles.default'),
        message: i18n.t('notifications:runtime.messages.default', {
          agentLabel,
          status: splitTechnicalWords(lifecycleStatus) || i18n.t('notifications:runtime.words.update'),
        }),
      };
  }
}

export function humanizeAlertTitle(summaryRaw?: string | null, alertNameRaw?: string | null): string {
  const summary = humanizeDetailText(summaryRaw, '');
  if (summary) return summary;

  const alertName = String(alertNameRaw || '').trim();
  if (!alertName) return i18n.t('notifications:alerts.defaultTitle');

  for (const [pattern, label] of getCommonAlertTitles()) {
    if (pattern.test(alertName)) return label;
  }

  return i18n.t('notifications:alerts.defaultTitle');
}

export function humanizeAlertDescription(descriptionRaw?: string | null, severity?: NotificationSeverity): string {
  const description = humanizeDetailText(descriptionRaw, '');
  if (description) return description;
  if (severity === 'critical') {
    return i18n.t('notifications:alerts.fallbackCriticalDescription');
  }
  return i18n.t('notifications:alerts.fallbackWarningDescription');
}

export function humanizeAlertSource(sourceRaw?: string | null): string {
  const source = String(sourceRaw || '').trim();
  if (!source) return i18n.t('notifications:alerts.sources.monitoring');
  if (source.includes(':') || source.includes('/')) return i18n.t('notifications:alerts.sources.monitoring');

  for (const [pattern, label] of getCommonAlertSources()) {
    if (pattern.test(source)) return label;
  }

  const normalized = splitTechnicalWords(source);
  return normalized ? uppercaseFirst(normalized) : i18n.t('notifications:alerts.sources.monitoring');
}

/**
 * Приводит заголовок и текст уведомления к человекочитаемому виду при отображении
 * (в т.ч. для записей, сохранённых до правок или пришедших с сырыми строками API).
 */
export function formatNotificationForDisplay(title: string, message: string): { title: string; message: string } {
  const rawTitle = String(title ?? '').trim();
  const rawMsg = String(message ?? '').trim();

  let titleOut = rawTitle;
  const batchLegacy =
    rawTitle.match(/^API (\d+) • ([A-Z]+) \((\d+) пунктов\)$/) ||
    rawTitle.match(/^API (\d+) • ([A-Z]+) \((\d+) details?\)$/i);
  if (batchLegacy) {
    titleOut = i18n.t('notifications:apiError.titleBatch', {
      status: batchLegacy[1],
      method: batchLegacy[2],
      count: Number(batchLegacy[3]),
    });
  } else {
    const single = rawTitle.match(/^API (\d+) • ([A-Z]+)$/);
    if (single) {
      titleOut = i18n.t('notifications:apiError.titleHttp', { status: single[1], method: single[2] });
    } else {
      const net = rawTitle.match(/^API error • ([A-Z]+)$/i);
      if (net) {
        titleOut = i18n.t('notifications:apiError.titleNetwork', { method: net[1] });
      } else {
        titleOut = humanizeDetailText(rawTitle, i18n.t('notifications:defaults.untitled'));
      }
    }
  }

  const messageOut = humanizeDetailText(rawMsg, i18n.t('notifications:defaults.noDescription'));

  return { title: titleOut, message: messageOut };
}

/**
 * Источники событий для Notification Center (без backend-рефакторинга):
 * - system: WS канал system:notifications, API /api/ws/metrics/alerts
 * - health: useSystemHealth (degraded/not_ready)
 * - build: failed strategy/indicator specs (useStrategySpecsFailedCatalog, useIndicatorSpecsFailedCatalog)
 * - backfill: ошибки Session START / backfill (503 из API при старте бэктеста/агента)
 * - signal: WS канал trading_signal по агенту
 * - execution: WS канал trading_execution по агенту
 * - user-action: зарезервировано для будущих действий пользователя
 */
